import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Award, Target, Heart, ArrowRight, Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import api from '../../services/api';

interface CompanyProfile {
  name: string;
  slogan: string;
  email: string;
  phone: string;
  address: string;
  logo_url?: string;
}

const MobileAboutSection: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<CompanyProfile>({
    name: 'IRIS SOFTECH',
    slogan: 'Solutions Digitales Innovantes',
    email: 'softechiris@gmail.com',
    phone: '771742350',
    address: 'Medina RUE 27 X 22 ENSUITE'
  });

  useEffect(() => {
    const loadCompanyProfile = async () => {
      try {
        const response = await api.get('/api/company/profile/');
        const data = response.data;
        setProfile({
          name: data.name || 'IRIS SOFTECH',
          slogan: data.slogan || 'Solutions Digitales Innovantes',
          email: 'softechiris@gmail.com',
          phone: '771742350',
          address: 'Medina RUE 27 X 22 ENSUITE',
          logo_url: data.logo_url
        });
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error);
      }
    };
    
    loadCompanyProfile();
  }, []);

  const values = [
    {
      icon: Target,
      title: 'Excellence technique',
      description: 'Technologies de pointe et meilleures pratiques'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Travail en étroite collaboration avec nos clients'
    },
    {
      icon: Award,
      title: 'Qualité',
      description: 'Tests rigoureux et documentation complète'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Équipe passionnée par l\'innovation'
    }
  ];

  const benefits = [
    'Devis gratuit et sans engagement',
    'Réponse sous 24h',
    'Accompagnement personnalisé',
    'Support technique inclus'
  ];

  return (
    <section className="bg-gray-50 px-4 py-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          À propos d'Iris Softech
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed">
          Fondée en 2020, agence spécialisée dans la création de solutions digitales sur mesure
        </p>
      </div>

      {/* Logo & Slogan */}
      <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
        <div className="flex flex-col items-center">
          <img 
            src={profile.logo_url || "/images/irislogobgremoved.png"} 
            alt={`${profile.name} Logo`} 
            className="w-32 h-32 object-contain mb-4"
          />
          <p className="text-center text-lg font-light text-gray-700 italic mb-4">
            {profile.slogan}
          </p>
          <button
            onClick={() => navigate('/aboutirissoftech')}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-medium shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <span>En savoir plus</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Notre vision */}
      <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Notre vision</h3>
        <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
          <p>
            Démocratiser l'accès aux technologies de pointe pour toutes les entreprises, 
            qu'elles soient startups ou grandes corporations.
          </p>
          <p>
            Plus de 50 entreprises accompagnées dans leur transformation digitale avec 
            des solutions qui génèrent de la valeur.
          </p>
        </div>
      </div>

      {/* Nos valeurs */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 text-center mb-4">Nos valeurs</h3>
        <div className="grid grid-cols-2 gap-3">
          {values.map((value, index) => (
            <div key={index} className="bg-white rounded-xl p-4 shadow-sm text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <value.icon className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {value.title}
              </h4>
              <p className="text-xs text-gray-600 leading-snug">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Coordonnées */}
      <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Nos coordonnées</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h5 className="text-sm font-semibold text-gray-900 mb-1">Email</h5>
              <a href={`mailto:${profile.email}`} className="text-sm text-blue-600 break-all">
                {profile.email}
              </a>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h5 className="text-sm font-semibold text-gray-900 mb-1">Téléphone</h5>
              <a href={`tel:${profile.phone.replace(/\s/g, '')}`} className="text-sm text-gray-600">
                {profile.phone}
              </a>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h5 className="text-sm font-semibold text-gray-900 mb-1">Adresse</h5>
              <p className="text-sm text-gray-600">{profile.address}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pourquoi nous choisir */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          Pourquoi choisir Iris Softech ?
        </h3>
        <div className="space-y-3">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-gray-900">{benefit}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MobileAboutSection;
